import {Component, DestroyRef, inject, Input} from '@angular/core';
import {
  Aangifte,
  AangifteRapport,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from '@angular/forms';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SpecZorgkostenComponent} from './spec-zorgkosten/spec-zorgkosten.component';
import {SpecGiftenComponent} from './spec-giften/spec-giften.component';
import {SpecScholingComponent} from './spec-scholing/spec-scholing.component';
import {SpecOverigePgaComponent} from './spec-overige-pga/spec-overige-pga.component';
import {CommonModule} from '@angular/common';
import {BehaviorSubject, filter, forkJoin, map, Observable, shareReplay, switchMap, take, tap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-pga-samenvatting',
    templateUrl: './pga-samenvatting.component.html',
    styleUrl: './pga-samenvatting.component.scss',
    imports: [
        SharedUiFormControlsModule,
        SharedUiDetailsModule,
        SpecGiftenComponent,
        SpecScholingComponent,
        SpecOverigePgaComponent,
        CommonModule,
        SpecZorgkostenComponent,
    ],
})
export class PgaSamenvattingComponent {
    @Input() public id!: string;
    public dataService = inject(BokkzIbDataService);
    private bokkzClient = inject(IbRekenService);
    private destroyRef = inject(DestroyRef);
    public optellingPga = 0;
    public aangifteRapport?: AangifteRapport;
    public dataVisible$ = new BehaviorSubject<boolean>(false);
    public optellingPga$ = new BehaviorSubject<number>(0);

    public formGroup = new FormGroup({
        terugontvangenAlimentatie: new FormControl<number | null>(0),
        inkomstenSpecifiekeZiektekosten: new FormControl<number | null>(0),
        inkomstenOverigStudiekosten: new FormControl<number | null>(0),
        inkomstenOverigGiften: new FormControl<number | null>(0),
        terugontvangenAlimentatieAangever: new FormControl<number | null>(0),
        inkomstenSpecifiekeZiektekostenAangever: new FormControl<number | null>(0),
        inkomstenOverigStudiekostenAangever: new FormControl<number | null>(0),
        inkomstenOverigGiftenAangever: new FormControl<number | null>(0),
        terugontvangenAlimentatiePartner: new FormControl<number | null>(0),
        inkomstenSpecifiekeZiektekostenPartner: new FormControl<number | null>(0),
        inkomstenOverigStudiekostenPartner: new FormControl<number | null>(0),
        inkomstenOverigGiftenPartner: new FormControl<number | null>(0),
        totaalNegatievePga: new FormControl<number | null>(0),
        totaalPga: new FormControl<number | null>(0),
    });

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public ngOnInit(): void {
        this.incomeTaxEntity$
            .pipe(
                map((entity) => entity.aangifte),
                filter((aangifte): aangifte is Aangifte => aangifte != null),
                take(1),
                tap((aangifte) => {
                    this.formGroup.reset({}, { emitEvent: false });
                    this.doBerekenNegatievePga(aangifte);
                }),
                switchMap((aangifte) => {
                    return forkJoin({
                        rapport: this.bokkzClient.getIncomeTaxReport(aangifte),
                        berekening: this.dataService.calculateLatestIncomeTaxReturn(),
                    });
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({ rapport, berekening }) => {
                this.aangifteRapport = rapport;
                const pga = berekening.opbouwPGA;
                if (pga !== undefined) {
                    this.optellingPga$.next(
                        (pga.specifiekeZorgkosten || 0) +
                            (pga.aftrekbareGiften || 0) +
                            (pga.scholingsuitgaven || 0) +
                            (pga.restantPgaVoorgaandeJaren || 0) +
                            (pga.onderhoudsverplichtingen || 0) +
                            (pga.weekenduitgaven || 0),
                    );
                    this.formGroup.patchValue({ totaalPga: this.optellingPga$.value }, { emitEvent: false });
                    console.log(this.optellingPga);
                    console.log(pga.aftrekbareGiften);
                }
                this.dataVisible$.next(true);
            });
    }

    private doBerekenNegatievePga(aangifte: Aangifte) {
        const pga = aangifte.box1?.negatievePersoonsgebondenAftrek;
        const terugontvangenAlimentatie = pga?.terugontvangenAlimentatie || 0;
        const inkomstenSpecifiekeZiektekosten = pga?.inkomstenSpecifiekeZiektekosten || 0;
        const inkomstenOverigGiften = pga?.inkomstenOverigGiften || 0;
        const inkomstenOverigStudiekosten = pga?.inkomstenOverigStudiekosten || 0;

        const pgaPartner = aangifte.partnerAangifte?.box1?.negatievePersoonsgebondenAftrek;
        const terugontvangenAlimentatiePartner = pgaPartner?.terugontvangenAlimentatie || 0;
        const inkomstenSpecifiekeZiektekostenPartner = pgaPartner?.inkomstenSpecifiekeZiektekosten || 0;
        const inkomstenOverigStudiekostenPartner = pgaPartner?.inkomstenOverigGiften || 0;
        const inkomstenOverigGiftenPartner = pgaPartner?.inkomstenOverigStudiekosten || 0;

        const totaalNegatievePga =
            terugontvangenAlimentatie +
            inkomstenSpecifiekeZiektekosten +
            inkomstenOverigGiften +
            inkomstenOverigStudiekosten +
            terugontvangenAlimentatiePartner +
            inkomstenSpecifiekeZiektekostenPartner +
            inkomstenOverigGiftenPartner +
            inkomstenOverigGiftenPartner;

        this.formGroup.patchValue(
            {
                terugontvangenAlimentatieAangever: terugontvangenAlimentatie || 0,
                inkomstenSpecifiekeZiektekostenAangever: inkomstenSpecifiekeZiektekosten || 0,
                inkomstenOverigGiftenAangever: inkomstenOverigGiften || 0,
                inkomstenOverigStudiekostenAangever: inkomstenOverigStudiekosten || 0,
                terugontvangenAlimentatiePartner: terugontvangenAlimentatiePartner || 0,
                inkomstenSpecifiekeZiektekostenPartner: inkomstenSpecifiekeZiektekostenPartner || 0,
                inkomstenOverigGiftenPartner: inkomstenOverigGiftenPartner || 0,
                inkomstenOverigStudiekostenPartner: inkomstenOverigStudiekostenPartner || 0,
                totaalNegatievePga: totaalNegatievePga || 0,
            },
            { emitEvent: false },
        );
    }
}
